import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useOTRoomStore from '../stores/otRoomStore';
import useDepartmentStore from '../stores/departmentStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

// B2 / S6–S7 — OT Room config catalog. Minimal on purpose: name / type /
// active only, NO cross-field interactions (stated explicitly in the stories
// so it isn't a silent miss). Config only — how many OTs exist for the later
// EPIC E booking board; no booking/schedule here. Org-global, no presets
// (rooms are hospital-specific).

const OT_ROOM_TYPES = [
  { value: 'major', label: 'Major OT' },
  { value: 'minor', label: 'Minor OT' },
  { value: 'labor_room', label: 'Labor room' },
];

const blankRow = () => ({ name: '', type: '', active: true });

function validate(row) {
  const e = {};
  if (!row.name || !row.name.trim()) e.name = 'Required';
  if (!row.type) e.type = 'Required';
  return e;
}

export default function OTRoomCatalogPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  const {
    otRooms,
    isLoading,
    error,
    includeInactive,
    fetchOTRooms,
    createOTRoom,
    updateOTRoom,
    deactivateOTRoom,
    setIncludeInactive,
    setOTRoomDepartments,
    clearError,
  } = useOTRoomStore();

  // B5 / S3 — org departments for the "Visible to" tag. GROUPING HINT ONLY:
  // OT boards always show every room (no department_id param), so the tag never
  // hides a theatre. Only ACTIVE departments are offered.
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);

  const [form, setForm] = useState(null); // null | {mode:'add'} | {mode:'edit', id}
  const [row, setRow] = useState(blankRow());
  const [orig, setOrig] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [banner, setBanner] = useState(null);
  // B5 — dept-tag selection for the current form; saved via the PUT in the same
  // Save click. `deptFilter` is the display-only list view-filter.
  const [deptIds, setDeptIds] = useState([]);
  const [deptFilter, setDeptFilter] = useState('');

  // Initial load — stable deps, single-flight stores (bounded requests).
  useEffect(() => {
    fetchOTRooms();
    fetchDepartments();
  }, [fetchOTRooms, fetchDepartments]);

  const change = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  const toggleDept = (deptId) => {
    setDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]
    );
  };

  // ── B5 (bug 3) — display-only list view-filter, MEMBERSHIP semantic: a dept
  // anchor shows ONLY rooms tagged to it; "Org-wide" shows ONLY untagged; empty
  // = every room. A tag NEVER hides a room from an OT BOARD — that's a separate
  // consumption surface (boards always show all); this is an admin convenience
  // list-filter only, so clearing restores every room. ──────────────
  const visibleRooms = useMemo(
    () => filterByDeptMembership(otRooms, deptFilter),
    [otRooms, deptFilter],
  );

  const openAdd = () => {
    setRow(blankRow());
    setOrig(null);
    setErrors({});
    setBanner(null);
    setDeptIds([]);
    setForm({ mode: 'add' });
  };

  const openEdit = (p) => {
    // Seed ONLY form fields — read-only keys never enter form state.
    setRow({ name: p.name || '', type: p.type || '', active: p.active !== false });
    setOrig(p);
    setErrors({});
    setBanner(null);
    setDeptIds(Array.isArray(p.department_ids) ? [...p.department_ids] : []);
    setForm({ mode: 'edit', id: p.ot_room_id });
  };

  const close = () => {
    setForm(null);
    setOrig(null);
    setErrors({});
    setDeptIds([]);
  };

  const submit = async () => {
    const e = validate(row);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);

    let result;
    if (form.mode === 'add') {
      result = await createOTRoom({ name: row.name.trim(), type: row.type });
    } else {
      // PATCH = allowlist DIFF vs the original row.
      const patch = {};
      if (row.name.trim() !== orig.name) patch.name = row.name.trim();
      if (row.type !== orig.type) patch.type = row.type;
      if ((row.active !== false) !== (orig.active !== false)) patch.active = row.active !== false;
      // B5 — a dept-tag-only edit must still save (tags travel via the separate
      // PUT). Only "nothing changed at all" is a no-op.
      const seededDepts = Array.isArray(orig.department_ids) ? orig.department_ids : [];
      const deptChanged =
        deptIds.length !== seededDepts.length || deptIds.some((d) => !seededDepts.includes(d));
      if (Object.keys(patch).length === 0 && !deptChanged) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'No changes to save' });
        return;
      }
      result = Object.keys(patch).length
        ? await updateOTRoom(form.id, patch)
        : { success: true, data: {} }; // dept-tag-only change → skip the PATCH
    }

    if (!result.success) {
      setBusy(false);
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }
    // B5 — tag departments as part of the SAME Save click, AFTER create/PATCH.
    const isAdd = form.mode === 'add';
    const otRoomId = isAdd ? result.data?.ot_room_id : form.id;
    if (!isAdd || deptIds.length > 0) {
      if (!otRoomId) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'OT room created, but departments could not be saved — edit it to set departments.' });
        close();
        return;
      }
      const deptResult = await setOTRoomDepartments(otRoomId, deptIds);
      setBusy(false);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `OT room ${isAdd ? 'created' : 'updated'}, but departments were not saved: ${deptResult.error || 'unknown error'}` });
        return;
      }
    } else {
      setBusy(false);
    }
    close();
    setBanner({ kind: 'ok', msg: isAdd ? 'OT room created' : 'OT room updated' });
  };

  const askDeactivate = (p) => {
    setConfirm({
      title: 'Deactivate OT room?',
      message: `"${p.name}" will be hidden from new use. Re-activate later by editing and toggling Active.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateOTRoom(p.ot_room_id);
        setBanner(
          r.success
            ? { kind: 'ok', msg: 'OT room deactivated' }
            : { kind: 'err', msg: r.error || 'Deactivate failed' }
        );
      },
    });
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  // B6 / S4 (Variant A) — add/edit form panel. Top for a new item; inline below
  // its source row for an edit. One element, one position per render (add XOR edit).
  const formPanel = form && (
    <div className="pcp-add-wrap">
      <div className="pcp-add-title">
        {form.mode === 'add' ? 'New OT Room' : `Edit · ${orig?.name || ''}`}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Name *</label>
          <input
            className={inputCls}
            aria-label="Name *"
            value={row.name}
            onChange={(e) => change('name', e.target.value)}
            placeholder="OT-1"
          />
          {errors.name && <div className="text-xs text-rose-600 mt-1">{errors.name}</div>}
        </div>
        <div>
          <label className={labelCls}>Type *</label>
          <select
            className={inputCls}
            aria-label="Type *"
            value={row.type}
            onChange={(e) => change('type', e.target.value)}
          >
            <option value="">—</option>
            {OT_ROOM_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {errors.type && <div className="text-xs text-rose-600 mt-1">{errors.type}</div>}
        </div>
        {form.mode === 'edit' && (
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 mt-5">
            <input
              type="checkbox"
              checked={row.active !== false}
              onChange={(e) => change('active', e.target.checked)}
            />
            Active
          </label>
        )}
      </div>

      {/* B5 / S3 — "Visible to" departments (optional, multi). GROUPING
          HINT ONLY: OT boards always show every theatre regardless of tag.
          Hidden on a dark org. Empty = org-wide. Saved via the PUT in the
          same Save click. */}
      {activeDepts.length > 0 && (
        <div className="mt-3">
          <label className={labelCls}>
            Visible to{' '}
            <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
              (optional, multi — leave empty for org-wide)
            </span>
          </label>
          <div className="flex flex-wrap gap-4">
            {activeDepts.map((d) => (
              <label key={d.dept_id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={deptIds.includes(d.dept_id)}
                  onChange={() => toggleDept(d.dept_id)}
                />
                {d.name}
              </label>
            ))}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Grouping hint — OT boards always show all theatres.
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button className="pcp-btn pcp-btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : form.mode === 'add' ? 'Create' : 'Save Changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={close} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Permission gate — all hooks above; redirect home like LabCatalogPage.
  // The server still 403s every write (defence-in-depth, S6 AC-NEG).
  if (!hasPermission('MANAGE_BED_CONFIG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🚪 OT Rooms
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Operation-theatre rooms available in your organisation.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={!!form}>
            + Add OT Room
          </button>
        </div>
        <FeesBillingTabs active="ot-rooms" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <div className="pcp-filter-bar">
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {activeDepts.length > 0 && (
            <select
              className={inputCls}
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          <span className="pcp-count">{visibleRooms.length} rooms</span>
        </div>

        {form?.mode === 'add' && formPanel}

        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); fetchOTRooms({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : otRooms.length === 0 ? (
          <div className="pcp-state">
            🚪 No OT rooms yet. Click + Add OT Room to add the first one.
          </div>
        ) : (
          <table className="pcp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRooms.map((p) => {
                const isEditing = form?.mode === 'edit' && form.id === p.ot_room_id;
                return (
                <React.Fragment key={p.ot_room_id}>
                <tr
                  className={`${p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}${isEditing ? ' pcp-row-editing' : ''}`}
                >
                  <td>
                    <div className="pcp-name">{p.name}</div>
                    <div className="pcp-subname">{p.ot_room_id}</div>
                  </td>
                  <td>{(OT_ROOM_TYPES.find((t) => t.value === p.type) || {}).label || p.type}</td>
                  <td>
                    <div className="pcp-chips">
                      {p.active === false ? (
                        <span className="pcp-chip pcp-chip-inactive">Deactivated</span>
                      ) : (
                        <span className="pcp-chip">Active</span>
                      )}
                      {/* B5 — dept tags (grouping hint only; boards show all). */}
                      {(p.department_ids || []).length > 0 ? (
                        <>
                          <span className="pcp-depts-caption">Visible to:</span>
                          {(p.department_ids || []).map((did) => (
                            <span
                              key={did}
                              className="pcp-chip"
                              style={{
                                background: isDark ? '#3730a3' : '#e0e7ff',
                                color: isDark ? '#e0e7ff' : '#3730a3',
                                borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                              }}
                            >
                              🏷 {deptNameById[did] || did}
                            </span>
                          ))}
                        </>
                      ) : activeDepts.length > 0 ? (
                        p.owner_scope === 'DEPARTMENT' ? (
                          <span className="pcp-depts-caption">Org-wide (tagged departments retired)</span>
                        ) : (
                          <span className="pcp-chip">Org-wide</span>
                        )
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="pcp-actions">
                      <button
                        className="pcp-btn pcp-btn-link"
                        onClick={() => openEdit(p)}
                        disabled={!!form}
                      >
                        Edit
                      </button>
                      {p.active !== false && (
                        <button
                          className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                          onClick={() => askDeactivate(p)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* B6 / S4 — Variant-A inline edit expands directly below its row */}
                {isEditing && (
                  <tr className="pcp-edit-row">
                    <td colSpan={4}>{formPanel}</td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel="Deactivate"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
